/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { custApi } from "../../lib/customerApi";
import { openRazorpay } from "../../lib/razorpay";
import {
  ShoppingBag, Clock, CheckCircle, Truck, Package, XCircle,
  ArrowRight, ArrowLeft, MapPin, Receipt, X, CreditCard, ShieldCheck, Phone, User,
} from "@phosphor-icons/react";

const STATUS_META = {
  placed:      { label: "Placed",      Icon: Clock,       color: "#0B2B66" },
  confirmed:   { label: "Confirmed",   Icon: CheckCircle, color: "#DC2626" },
  in_progress: { label: "On the way",  Icon: Truck,       color: "#C9A227" },
  delivered:   { label: "Delivered",   Icon: Package,     color: "#7F1D1D" },
  cancelled:   { label: "Cancelled",   Icon: XCircle,     color: "#7A2E18" },
};

// Category filters — matches partner_subcategory persisted on each order.
const CATEGORY_FILTERS = {
  all:       { label: "All orders",      subs: null },
  biohazard: { label: "Biohazard store", subs: ["biohazard_store"] },
  pharmacy:  { label: "Pharmacy",         subs: ["pharmacy"] },
  equipment: { label: "Equipment",        subs: ["equipment_sale", "equipment_rental"] },
};

export default function MeOrders() {
  const [orders, setOrders] = useState(null);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const catMeta = CATEGORY_FILTERS[category] || CATEGORY_FILTERS.all;

  const load = async () => {
    try {
      const r = await custApi.get("/me/orders");
      setOrders(r.data.orders || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load orders"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!orders) return null;
    if (!catMeta.subs) return orders;
    return orders.filter(o => catMeta.subs.includes(o.partner_subcategory));
  }, [orders, catMeta]);

  const cancel = async (o) => {
    if (!window.confirm(`Cancel ${o.order_no}?`)) return;
    try {
      await custApi.post(`/me/orders/${o.id}/cancel`);
      toast.success("Order cancelled");
      load();
      setSelected(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  const payNow = async (o) => {
    try {
      const chk = await custApi.post(`/me/orders/${o.id}/checkout`);
      if (!chk.data.key_id) {
        toast.error("Online payment not configured — please use cash on delivery");
        return;
      }
      const result = await openRazorpay(chk.data);
      await custApi.post(`/me/orders/${o.id}/verify-payment`, {
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });
      toast.success(`Payment successful · ${o.order_no}`);
      load();
      setSelected(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      if (msg !== "Payment cancelled") toast.error(msg || "Payment failed");
    }
  };

  if (!orders) {
    return <div className="text-sm text-[#8A8780]">Loading orders…</div>;
  }

  return (
    <div className="space-y-5 px-4 py-4" data-testid="me-orders">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
            {catMeta.label}
          </div>
          <h1 className="font-heading text-2xl text-[#1A1A1A]">Order history</h1>
        </div>
        <Link to="/me/services" className="inline-flex items-center gap-1 text-xs text-[#5C5A55] hover:text-[#DC2626]">
          <ShoppingBag size={11}/> Browse services
        </Link>
      </header>

      {/* Category filter chips */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1" data-testid="me-orders-filter">
        {Object.entries(CATEGORY_FILTERS).map(([key, meta]) => {
          const on = key === category;
          const count = key === "all"
            ? orders.length
            : orders.filter(o => (meta.subs || []).includes(o.partner_subcategory)).length;
          return (
            <button key={key}
                    data-testid={`me-orders-tab-${key}`}
                    onClick={() => {
                      const p = new URLSearchParams(params);
                      if (key === "all") p.delete("category");
                      else p.set("category", key);
                      setParams(p, { replace: true });
                    }}
                    className={`h-9 px-3 shrink-0 rounded-full text-[12px] font-semibold border transition-colors ${
                      on
                        ? "bg-[#DC2626] text-white border-[#DC2626]"
                        : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                    }`}>
              {meta.label}
              <span className={`ml-1.5 text-[10px] font-mono ${on ? "text-white/80" : "text-[#9CA3AF]"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-[#E5E2D9] rounded-xl p-8 text-center">
          <div className="text-sm text-[#5C5A55] mb-3">
            {category === "biohazard"
              ? "No biohazard-store orders yet."
              : "You haven't placed any orders yet."}
          </div>
          <Link to={category === "biohazard" ? "/me/services/biohazard" : "/me/services"}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded bg-[#DC2626] text-white text-xs font-medium">
            {category === "biohazard" ? "Browse biohazard store" : "Browse services"} <ArrowRight size={11} weight="bold"/>
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(o => {
            const m = STATUS_META[o.status] || STATUS_META.placed;
            return (
              <button key={o.id} onClick={() => setSelected(o)}
                       data-testid={`order-${o.id}`}
                       className="w-full text-left bg-white border border-[#E5E2D9] rounded-md p-4 flex items-center gap-3 hover:border-[#DC2626] transition">
                <div className="w-10 h-10 rounded-md grid place-items-center flex-none"
                      style={{ background: m.color + "20", color: m.color }}>
                  <m.Icon size={14} weight="duotone"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-[10px] text-[#8A8780]">{o.order_no}</div>
                    {o.partner_subcategory && (
                      <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#334155] font-bold">
                        {o.partner_subcategory.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  <div className="font-heading text-base text-[#1A1A1A] truncate">{o.partner_name}</div>
                  <div className="text-[11px] text-[#5C5A55]">
                    {o.items.length} item{o.items.length > 1 ? "s" : ""} · ₹{o.total.toFixed(2)}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded"
                      style={{ background: m.color + "20", color: m.color }}>
                  {m.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <OrderDetailDrawer o={selected} onClose={() => setSelected(null)}
                            onCancel={() => cancel(selected)}
                            onPayNow={() => payNow(selected)}/>
      )}
    </div>
  );
}

function OrderDetailDrawer({ o, onClose, onCancel, onPayNow }) {
  const m = STATUS_META[o.status] || STATUS_META.placed;
  const canCancel = ["placed", "confirmed"].includes(o.status);
  const canPay = o.payment_status !== "paid"
                  && o.status !== "cancelled"
                  && o.status !== "delivered";
  const [delivery, setDelivery] = useState(null);

  useEffect(() => {
    // Fetch the delivery task lazily once the drawer mounts so the OTP box
    // appears for any order that's already left the storefront.
    custApi.get(`/me/orders/${o.id}/delivery`)
      .then(r => setDelivery(r.data.task))
      .catch(() => setDelivery(null));
    // eslint-disable-next-line
  }, [o.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-3"
          onClick={onClose} data-testid="order-detail">
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-auto"
            onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] text-[#8A8780]">{o.order_no}</div>
            <div className="font-heading text-xl">{o.partner_name}</div>
            <span className="inline-flex items-center gap-1.5 mt-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded"
                  style={{ background: m.color + "20", color: m.color }}>
              <m.Icon size={10} weight="fill"/> {m.label}
            </span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55] mb-2">Items</div>
          <div className="space-y-1.5">
            {o.items.map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-3 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="text-[#1A1A1A] font-medium">{it.name}</div>
                  <div className="text-[10px] text-[#8A8780]">
                    {it.qty} × {it.type === "rental" ? `${it.rental_days || 1} day(s) @ ₹${it.rate_used}/day` : `₹${it.unit_price}`}
                  </div>
                </div>
                <div className="font-mono text-[#1A1A1A]">₹{it.line_total.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1 text-xs border-t border-[#F0ECE3] pt-3">
          <div className="flex justify-between"><span className="text-[#5C5A55]">Subtotal</span><span className="font-mono">₹{o.subtotal.toFixed(2)}</span></div>
          {o.deposit_total > 0 && (
            <div className="flex justify-between"><span className="text-[#5C5A55]">Refundable deposit</span><span className="font-mono">₹{o.deposit_total.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between font-medium pt-1 border-t border-[#F0ECE3]">
            <span>Total</span>
            <span className="font-mono text-base">₹{o.total.toFixed(2)}</span>
          </div>
          <div className="text-[10px] text-[#8A8780] uppercase tracking-[0.18em] pt-1">
            Payment · {o.payment_method.toUpperCase()} · {o.payment_status}
          </div>
        </div>

        <div className="space-y-1 text-xs border-t border-[#F0ECE3] pt-3">
          <div className="flex items-start gap-2">
            <MapPin size={11} className="text-[#8A8780] mt-0.5 flex-none"/>
            <span>{o.delivery_address}</span>
          </div>
          {o.customer_notes && (
            <div className="flex items-start gap-2">
              <Receipt size={11} className="text-[#8A8780] mt-0.5 flex-none"/>
              <span>{o.customer_notes}</span>
            </div>
          )}
        </div>

        {delivery && delivery.status !== "pending" && delivery.status !== "delivered" && (
          <div className="bg-[#FEE2E2] border border-[#DC2626]/20 rounded-md p-3.5 space-y-2.5"
                data-testid="delivery-box">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#DC2626] font-semibold">
                  Delivery in progress
                </div>
                <div className="text-xs text-[#7F1D1D] mt-0.5 capitalize">
                  {delivery.status.replace(/_/g, " ")}
                </div>
              </div>
              <Truck size={20} weight="duotone" className="text-[#DC2626]"/>
            </div>
            <div className="bg-white rounded p-3 text-center">
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#5C5A55] mb-1">
                Share this OTP at the door
              </div>
              <div className="font-mono text-3xl tracking-[0.3em] text-[#DC2626]"
                    data-testid="delivery-otp">{delivery.otp}</div>
            </div>
            {delivery.staff_name && (
              <div className="text-xs text-[#7F1D1D] space-y-1">
                <div className="flex items-center gap-1.5"><User size={11}/> {delivery.staff_name}</div>
                {delivery.staff_phone && (
                  <a href={`tel:${delivery.staff_phone}`} className="inline-flex items-center gap-1.5 underline">
                    <Phone size={11}/> {delivery.staff_phone}
                  </a>
                )}
              </div>
            )}
            <div className="text-[10px] text-[#5C5A55] italic flex items-center gap-1">
              <ShieldCheck size={10}/> Only share the OTP once the goods are in your hands.
            </div>
          </div>
        )}

        {canPay && (
          <button onClick={onPayNow} data-testid="pay-now"
                   className="w-full h-11 rounded bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-medium inline-flex items-center justify-center gap-2">
            <CreditCard size={13} weight="bold"/> Pay ₹{o.total.toFixed(2)} online
          </button>
        )}

        {canCancel && (
          <button onClick={onCancel} data-testid="cancel-order"
                   className="w-full h-10 rounded border border-[#F4C7B9] text-[#7A2E18] text-xs hover:bg-[#FBE7E0]">
            Cancel order
          </button>
        )}
      </div>
    </div>
  );
}
